import * as React from 'react';
import { DayPicker, type DateRange } from '@daypicker/react';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import { cn } from '../../utils';
import type { Locale } from 'date-fns';
import type { DateInputSize } from './date-picker-shared';
import {
  DISPLAY_FORMAT,
  ISO_FORMAT,
  calendarClassNames,
  dateInputClassName,
  dateInputErrorClassName,
  dateInputSizeClassName,
  maskDateInput,
  parseInputDate,
  parseIsoDate,
  selectedDayClassName,
  useCalendarPopoverStyle,
  useCloseOnOutsideClick,
} from './date-picker-shared';
import { Button } from './button';
import { CalendarPopover } from './calendar-popover';

export interface DateRangeValue {
  from?: string; // ISO YYYY-MM-DD
  to?: string; // ISO YYYY-MM-DD; missing = open-ended range
}

export interface DateRangePickerProps {
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  placeholderFrom?: string;
  placeholderTo?: string;
  disabled?: boolean;
  error?: boolean;
  /** The from-input gets `id`, the to-input gets `${id}-end`. */
  id?: string;
  locale?: Locale;
  /** @default "default" */
  size?: DateInputSize;
  /** Calendar footer cancel button text. @default "Cancel" */
  cancelLabel?: string;
  /** Calendar footer apply button text. @default "Apply" */
  applyLabel?: string;
  /** Sizes the from/to/calendar row — e.g. `w-72` in a toolbar. */
  className?: string;
}

/** Swap endpoints when both are set and out of order. */
const sortRange = (next: DateRangeValue): DateRangeValue =>
  next.from && next.to && next.to < next.from
    ? { from: next.to, to: next.from }
    : next;

/**
 * Sibling of DatePicker for [from, to] ranges: two masked text inputs sharing
 * one calendar in `mode="range"`. The controlled value is a pair of ISO
 * strings; an empty endpoint means unset (e.g. ongoing absence).
 *
 * The calendar anchors on the lone set endpoint — or today, shown
 * pre-selected, when the range is empty — so a single click completes the
 * range against that anchor. A click while a complete range is shown
 * restarts a new range from the clicked day. Calendar clicks only build a
 * draft, previewed in the inputs: the apply button commits it through
 * `onChange`; the cancel button or an outside click discards it. Typed input
 * commits stay immediate.
 */
const DateRangePicker = React.forwardRef<
  HTMLInputElement,
  DateRangePickerProps
>(
  (
    {
      value,
      onChange,
      placeholderFrom,
      placeholderTo,
      disabled,
      error,
      id,
      locale,
      size = 'default',
      cancelLabel = 'Cancel',
      applyLabel = 'Apply',
      className,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [fromInput, setFromInput] = React.useState('');
    const [toInput, setToInput] = React.useState('');
    // Pending calendar selection; null mirrors the committed value.
    const [draft, setDraft] = React.useState<DateRangeValue | null>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const popoverRef = React.useRef<HTMLDivElement>(null);
    const focusedField = React.useRef<'from' | 'to' | null>(null);
    const popoverStyle = useCalendarPopoverStyle(open, containerRef);

    const effectiveRange = draft ?? value;
    const effectiveFrom = React.useMemo(
      () => parseIsoDate(effectiveRange?.from),
      [effectiveRange?.from],
    );
    const effectiveTo = React.useMemo(
      () => parseIsoDate(effectiveRange?.to),
      [effectiveRange?.to],
    );

    // Visual anchor when the range is empty. Recomputed per render on purpose
    // (memoizing would pin a stale day across midnight on long-lived pages);
    // RDP matches days with isSameDay, so time-of-day is irrelevant.
    const today = new Date();

    const fromDisplay = effectiveFrom
      ? format(effectiveFrom, DISPLAY_FORMAT)
      : '';
    const toDisplay = effectiveTo ? format(effectiveTo, DISPLAY_FORMAT) : '';

    React.useEffect(() => {
      if (focusedField.current !== 'from') setFromInput(fromDisplay);
    }, [fromDisplay]);
    React.useEffect(() => {
      if (focusedField.current !== 'to') setToInput(toDisplay);
    }, [toDisplay]);

    const emit = React.useCallback(
      (next: DateRangeValue) => {
        onChange?.(sortRange(next));
      },
      [onChange],
    );

    /** Discard the draft and close — cancel button and outside click. */
    const cancel = React.useCallback(() => {
      setDraft(null);
      setOpen(false);
    }, []);

    useCloseOnOutsideClick(open, containerRef, popoverRef, cancel);

    const apply = () => {
      // Skip emit when nothing was drafted — call sites hang side effects
      // off onChange (e.g. the VAT dialog clears its period preset).
      if (draft) emit(draft);
      setDraft(null);
      setOpen(false);
    };

    const commitField = React.useCallback(
      (field: 'from' | 'to') => {
        const raw = (field === 'from' ? fromInput : toInput).trim();
        const setInput = field === 'from' ? setFromInput : setToInput;
        const display = field === 'from' ? fromDisplay : toDisplay;

        if (!raw) {
          // Clearing an input clears that endpoint only. Typed commits are
          // authoritative: emit immediately, absorbing any calendar draft
          // (the inputs preview it, so committing what's shown is coherent).
          emit({ ...effectiveRange, [field]: undefined });
          setDraft(null);
          setInput('');
          return;
        }

        const parsed = parseInputDate(raw);
        if (!parsed) {
          setInput(display);
          return;
        }

        emit({ ...effectiveRange, [field]: format(parsed, ISO_FORMAT) });
        setDraft(null);
        setInput(format(parsed, DISPLAY_FORMAT));
      },
      [effectiveRange, emit, fromDisplay, fromInput, toDisplay, toInput],
    );

    const handleSelect = (_range: DateRange | undefined, triggerDate: Date) => {
      // Clicks only build the draft; apply commits, cancel/outside discards.
      // A click while a complete range is shown starts a new range.
      if (effectiveFrom && effectiveTo) {
        setDraft({ from: format(triggerDate, ISO_FORMAT), to: undefined });
        return;
      }
      // One click completes the range against the anchor: the lone existing
      // endpoint, or today (shown pre-selected when the range is empty).
      // RDP's computed range is ignored — it's undefined when only `to` is
      // set. `sortRange` swaps out-of-order endpoints, so a click before the
      // anchor becomes the start and a same-day click yields from === to.
      const anchor = effectiveFrom ?? effectiveTo ?? today;
      setDraft(
        sortRange({
          from: format(anchor, ISO_FORMAT),
          to: format(triggerDate, ISO_FORMAT),
        }),
      );
    };

    const inputProps = (field: 'from' | 'to') => ({
      type: 'text' as const,
      disabled,
      inputMode: 'numeric' as const,
      onFocus: () => {
        focusedField.current = field;
      },
      onBlur: () => {
        focusedField.current = null;
        commitField(field);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitField(field);
        }
      },
      className: cn(
        dateInputClassName,
        // Flex items default to `min-width: auto`, which for a text input is
        // its intrinsic ~20-character width — the two inputs would refuse to
        // shrink inside a caller-set `className` width.
        'min-w-0',
        dateInputSizeClassName[size],
        error && dateInputErrorClassName,
      ),
    });

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <div className="flex items-center gap-2">
          <input
            ref={ref}
            id={id}
            value={fromInput}
            placeholder={placeholderFrom}
            onChange={(e) => setFromInput(maskDateInput(e.target.value))}
            {...inputProps('from')}
          />
          <span className="text-muted-foreground">–</span>
          <input
            id={id ? `${id}-end` : undefined}
            value={toInput}
            placeholder={placeholderTo}
            onChange={(e) => setToInput(maskDateInput(e.target.value))}
            {...inputProps('to')}
          />
          <button
            type="button"
            aria-label="Open calendar"
            aria-expanded={open}
            aria-haspopup="dialog"
            disabled={disabled}
            onClick={() => {
              // Opening starts from a fresh mirror; closing via the toggle
              // discards like cancel.
              setDraft(null);
              setOpen((current) => !current);
            }}
            className={cn(
              'flex shrink-0 cursor-pointer items-center justify-center rounded-xl border-2 border-border bg-muted text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              size === 'sm' ? 'size-9' : 'h-12 w-11',
            )}
          >
            <Calendar className="size-4 shrink-0 opacity-70" />
          </button>
        </div>

        <CalendarPopover
          open={open}
          popoverRef={popoverRef}
          style={popoverStyle}
        >
          <DayPicker
            mode="range"
            showOutsideDays
            selected={
              effectiveFrom || effectiveTo
                ? { from: effectiveFrom, to: effectiveTo }
                : { from: today, to: undefined }
            }
            onSelect={handleSelect}
            defaultMonth={effectiveFrom ?? effectiveTo}
            locale={locale}
            classNames={{
              ...calendarClassNames,
              // `selected` applies to every range day (start/middle/end) in
              // DayPicker v10. Keep it colorless on a complete range so the range_*
              // keys below own the look — otherwise its `!text-background`
              // bleeds near-white text onto the light range_middle cells
              // (unreadable). While the range is incomplete no range_middle
              // exists and RDP applies range_start/end only when both ends
              // are set, so the lone endpoint takes the dark look itself.
              selected:
                effectiveFrom && effectiveTo
                  ? 'font-semibold rounded-lg'
                  : selectedDayClassName,
              range_start: selectedDayClassName,
              range_end: selectedDayClassName,
              range_middle: '!bg-primary/15 !text-foreground font-medium',
            }}
          />
          <div className="flex justify-end gap-2 px-3 pb-3">
            <Button type="button" variant="outline" size="sm" onClick={cancel}>
              {cancelLabel}
            </Button>
            <Button type="button" size="sm" onClick={apply}>
              {applyLabel}
            </Button>
          </div>
        </CalendarPopover>
      </div>
    );
  },
);
DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
